import * as React from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    MenuItem,
} from "@mui/material";
import { main_button, MainInput } from "./Styles";
import { getRequest, postRequest } from "../../core/network";
import endpoints from "../../constants/endPoints.json";
import { useTranslation } from "react-i18next";
import { notification } from "antd";
import { AddPhotoAlternate, Close } from "@mui/icons-material";

const MAX_TICKET_IMAGES = 2;

async function uploadImageToPresigned(file, token, keyPrefix = "help-desk/tickets/new") {
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const key = `${keyPrefix}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const fileType = file.type || (ext === "png" ? "image/png" : "image/jpeg");
    const { data } = await getRequest({ fileType, key }, undefined, token, endpoints.partner.uploadsUrl);
    await fetch(data.uploadURL, { method: "PUT", headers: { "Content-Type": fileType }, body: file });
    return data.publicURL;
}

const TICKET_TYPE_LABELS = {
    "General Inquiry": "helpDesk.ticketTypeGeneralInquiry",
    Order: "helpDesk.ticketTypeOrder",
    Product: "helpDesk.ticketTypeProduct",
    Finance: "helpDesk.ticketTypeFinance",
    Inventory: "helpDesk.ticketTypeInventory",
};

// Ticket sub-types per ticketType, based on provided CSV.
// - Order: combines "Orders ( fulfillment )" and "Orders ( Shipping )" categories.
// - Inventory: combines "Inbound", "Outbound", and "Inventory Count" categories.
const TICKET_SUBTYPE_OPTIONS = {
    Order: [
        "Cancelation & Modification",
        "Wrong Item Delivered",
        "Wrong Quantity Delivered",
        "Delay",
        "Return Inspection",
        "Packaging Mistake",
        "COD",
        "Fake Attempts",
        "Damaged",
        "Lost",
    ],
    Inventory: [
        "Wrong Quantity",
        "Wrong SKU",
        "Unexpected SKUs",
        "Delay",
        "Packing Items",
        "Damaged Stock",
        "Cycle count request",
        "Missing Inventory",
    ],
    Product: [
        "Cycle count request",
        "Missing Inventory",
        "Damaged Stock",
    ],
};

// Translation keys for ticket subtypes. The underlying value sent to the backend
// remains the English label from TICKET_SUBTYPE_OPTIONS to avoid breaking APIs.
const TICKET_SUBTYPE_LABEL_KEYS = {
    "Cancelation & Modification": "helpDesk.subtype.cancelationModification",
    "Wrong Item Delivered": "helpDesk.subtype.wrongItemDelivered",
    "Wrong Quantity Delivered": "helpDesk.subtype.wrongQuantityDelivered",
    Delay: "helpDesk.subtype.delay",
    "Return Inspection": "helpDesk.subtype.returnInspection",
    "Packaging Mistake": "helpDesk.subtype.packagingMistake",
    COD: "helpDesk.subtype.cod",
    "Fake Attempts": "helpDesk.subtype.fakeAttempts",
    Damaged: "helpDesk.subtype.damaged",
    Lost: "helpDesk.subtype.lost",
    "Wrong Quantity": "helpDesk.subtype.wrongQuantity",
    "Wrong SKU": "helpDesk.subtype.wrongSku",
    "Unexpected SKUs": "helpDesk.subtype.unexpectedSkus",
    "Packing Items": "helpDesk.subtype.packingItems",
    "Damaged Stock": "helpDesk.subtype.damagedStock",
    "Cycle count request": "helpDesk.subtype.cycleCountRequest",
    "Missing Inventory": "helpDesk.subtype.missingInventory",
    Other: "helpDesk.subtype.other",
};

/**
 * Modal to create a help-desk ticket. Use in-place on any page (no redirect).
 * - context: null | { ticketType, relevantOrder?, relevantProduct?, relevantTransaction?, relevantInbound? }
 *   When null or General with no IDs: general ticket, type is fixed (no selector).
 *   When context has a specific type + id: ticket is linked to that entity; type is not selectable.
 */
export default function CreateTicketModal({ open, onClose, token, context, onSuccess }) {
    const { t } = useTranslation();
    const [subject, setSubject] = React.useState("");
    const [description, setDescription] = React.useState("");
    const [attachments, setAttachments] = React.useState([]);
    const [uploadingImage, setUploadingImage] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [ticketSubtype, setTicketSubtype] = React.useState("");

    const isGeneral = !context?.ticketType || context.ticketType === "General Inquiry";
    const hasContext = context && context.ticketType && context.ticketType !== "General Inquiry";
    const ticketType = hasContext ? context.ticketType : "General Inquiry";

    const availableSubtypes = React.useMemo(() => {
        const base = TICKET_SUBTYPE_OPTIONS[ticketType] || [];
        return base.includes("Other") ? base : [...base, "Other"];
    }, [ticketType]);

    const resetForm = React.useCallback(() => {
        setSubject("");
        setDescription("");
        setAttachments([]);
        setTicketSubtype("");
    }, []);

    const handleImageSelect = async (e) => {
        const file = e.target?.files?.[0];
        if (!file || !token || attachments.length >= MAX_TICKET_IMAGES) return;
        if (!file.type.startsWith("image/")) {
            notification.warning({ message: t("helpDesk.imagesOnly") });
            return;
        }
        e.target.value = "";
        setUploadingImage(true);
        try {
            const publicURL = await uploadImageToPresigned(file, token);
            setAttachments((prev) => [...prev, publicURL]);
        } catch (err) {
            notification.error({ message: err?.response?.data?.message || t("common.error") });
        } finally {
            setUploadingImage(false);
        }
    };

    const removeAttachment = (index) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    React.useEffect(() => {
        if (open) resetForm();
    }, [open, resetForm]);

    const handleClose = () => {
        if (submitting) return;
        onClose();
        resetForm();
    };

    const handleSubmit = () => {
        if (!subject.trim() || !description.trim() || !token) {
            notification.warning({ message: t("helpDesk.subjectAndDescriptionRequired") });
            return;
        }
        if (uploadingImage) {
            notification.warning({ message: t("helpDesk.waitForAttachments") });
            return;
        }
        setSubmitting(true);
        const body = {
            subject: subject.trim(),
            description: description.trim(),
            ticketType,
            ...(ticketSubtype && { ticketSubtype }),
            ...(attachments.length > 0 && { attachments }),
            ...(context?.relevantOrder && { relevantOrder: context.relevantOrder }),
            ...(context?.relevantProduct && { relevantProduct: context.relevantProduct }),
            ...(context?.relevantTransaction && { relevantTransaction: context.relevantTransaction }),
            ...(context?.relevantInbound && { relevantInbound: context.relevantInbound }),
        };
        postRequest(body, undefined, undefined, token, endpoints.partner.helpDeskTickets)
            .then(() => {
                notification.success({ message: t("helpDesk.ticketCreated") });
                handleClose();
                onSuccess?.();
            })
            .catch((err) => {
                const data = err?.response?.data;
                const code = data?.code;
                const apiMessage = data?.message;
                let message = t("helpDesk.createTicketFailed");
                if (code === "DUPLICATE_TICKET") {
                    message = t("helpDesk.duplicateTicket");
                } else if (apiMessage && typeof apiMessage === "string") {
                    message = apiMessage;
                }
                notification.error({ message });
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { backgroundColor: "var(--modal-bg)", backgroundImage: "none", color: "var(--tx)", opacity: 1 } }}
        >
            <DialogTitle>{t("helpDesk.newTicket")}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                    {/* General tickets: no type selection. Contextual tickets: show linked type only (read-only). */}
                    {isGeneral && !hasContext && (
                        <Typography variant="body2" sx={{ color: "var(--textSecondary)" }}>
                            {t("helpDesk.ticketTypeGeneralInquiry")}
                        </Typography>
                    )}
                    {hasContext && (
                        <Typography variant="body2" sx={{ color: "var(--textSecondary)" }}>
                            {t("helpDesk.linkedTo")}: {t(TICKET_TYPE_LABELS[ticketType] || "helpDesk.ticketTypeGeneralInquiry")}
                        </Typography>
                    )}
                    {availableSubtypes.length > 0 && (
                        <MainInput
                            label={t("helpDesk.ticketSubtype") || "Ticket subtype"}
                            fullWidth
                            select
                            value={ticketSubtype}
                            onChange={(e) => setTicketSubtype(e.target.value)}
                        >
                            {availableSubtypes.map((subtype) => (
                                <MenuItem key={subtype} value={subtype}>
                                    {t(TICKET_SUBTYPE_LABEL_KEYS[subtype] || subtype)}
                                </MenuItem>
                            ))}
                        </MainInput>
                    )}
                    <MainInput
                        label={t("helpDesk.subject")}
                        fullWidth
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        required
                    />
                    <MainInput
                        label={t("common.description")}
                        fullWidth
                        multiline
                        rows={4}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        required
                    />
                    <Box>
                        <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 1 }}>
                            {t("helpDesk.attachments")} ({attachments.length}/{MAX_TICKET_IMAGES})
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                            {attachments.map((url, index) => (
                                <Box key={url} sx={{ position: "relative", width: 72, height: 72 }}>
                                    <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
                                    <IconButton size="small" onClick={() => removeAttachment(index)} sx={{ position: "absolute", top: -8, right: -8, bgcolor: "var(--appBg)", "&:hover": { bgcolor: "var(--cardBg)" }, width: 24, height: 24 }}><Close fontSize="small" /></IconButton>
                                </Box>
                            ))}
                            {attachments.length < MAX_TICKET_IMAGES && (
                                <Button component="label" variant="outlined" size="small" disabled={uploadingImage} sx={{ minWidth: 72, height: 72, borderStyle: "dashed" }}>
                                    <input type="file" accept="image/*" hidden onChange={handleImageSelect} />
                                    <AddPhotoAlternate sx={{ color: "var(--textSecondary)" }} />
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting} sx={{ ...main_button }}>
                    {t("common.cancel")}
                </Button>
                <Button onClick={handleSubmit} disabled={!subject.trim() || !description.trim() || submitting || uploadingImage} sx={{ ...main_button }}>
                    {submitting ? t("common.submitting") : t("common.submit")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
